/**
 * The tail spring in the viewport — ADR-038: **the Body tab's spring control,
 * the chain it declares, and the layered drive the reference solver plays over
 * whatever clip runs** (R5, ruled 2026-09-24: "the spring follows the
 * animation").
 *
 * WHERE THE CHAIN COMES FROM, in order:
 *   1. a tail this session GREW (T2's `tailGrafts`) — the declaration is the
 *      tail prior's (`tailSpringDeclaration`), its colliders fitted from this
 *      character's flesh at bind (`fitTailColliders`);
 *   2. a file that DECLARES one (`VRMC_springBone`, read at the load door) —
 *      adopted as written, never re-derived; the knobs retune its joints;
 *   3. a tail the file RIGS but springs nothing on (bones named tail, chained off the
 *      body — the intake's `tail-rigid`, INTAKE_GUIDED_FLOW §3.4 C2): the tail prior's
 *      declaration over that chain, its radii measured from the flesh each bone owns.
 *      OFF until a person turns it on — an offer never acts.
 * None → nothing to offer, and the Body tab shows no spring.
 *
 * THE SETTINGS LIVE IN THE DOCUMENT (R3, ruled): on, stiffness, drag, gravity,
 * muscle, mood, the wag's knobs, inertia and the reflex flag are
 * `edits.tailSpring`, written by `tailSpring.set` — undoable, saved, and what
 * the export declares. ABSENT = the source decides: a grown tail wears the
 * prior's defaults, a file's spring what the file declares (its drive block,
 * or — a plain VRM spring — the passive reference with no drive at all). A
 * slider drag PREVIEWS (session only) and commits one set on release.
 *
 * THE MOTION is never Document state: it rides the viewport's render-preview
 * slot, applied before a frame renders and restored after, so the export, the
 * package and the pose tool always read the chain at rest. With the spring ON
 * the drive (the mood, over whatever the clip keys) is the spring's TARGET and
 * physics layers around it; OFF, the drive plays kinematically — the preview
 * wag, now one mood of six.
 *
 * THE CLOCK. `advance(frameDt)` runs the runtime's fixed 60 Hz accumulator
 * with the pose HELD per render frame — the viewport owns the mixer. A scrub
 * (the clip's time jumping, or moving while paused) or a clip restart is a
 * teleport: the chain resets onto its target and pre-rolls 0.5 s at the held
 * pose, so a paused frame shows a settled tail, not a whip.
 *
 * Depends on: three, adapter-three (runtime, drive, colliders, declared
 * springs), kernel (declarations, drive). Depended on by: main (the render
 * slot, the export door, the Body tab).
 */
import { Quaternion, Vector3, type Object3D, type SkinnedMesh } from 'three';
import {
  createSpringRuntime,
  createTailDrive,
  declaredSprings,
  fitTailColliders,
  measureDriveAxes,
  measureTailFlatness,
  tailHingeFromFlatness,
  type DriveAxes,
  type SpringRuntime,
  type TailColliderReport,
  type TailDrive,
} from '@harryhausen/adapter-three';
import {
  moodGains,
  retuneTailSpring,
  springDriveDeclaration,
  tailSpringDeclaration,
  TAIL_DYNAMICS_DEFAULTS,
  TAIL_MOOD_DEFAULT,
  TAIL_MUSCLE_DEFAULT,
  TAIL_SPRING_KNOBS,
  TAIL_WAG_DEFAULTS,
  type SpringDeclaration,
  type SpringKnobs,
  type TailHinge,
  type TailSpringSettings,
} from '@harryhausen/kernel';

/** What the controller needs of a run — the scene, the grown tails, and the rig's mapping. */
export interface TailSpringRun {
  readonly scene: Object3D;
  readonly rig: { readonly mapping: readonly { readonly ontologyId: string; readonly rigBone: string }[] };
  readonly tailGrafts: readonly { readonly bones: readonly string[]; readonly radii: readonly number[] }[];
}

export type TailSpringSource = 'grown' | 'file' | 'native';

/** The settings a person moves, without the attribution (the controller adds it at commit). */
export type TailSpringChoice = Omit<TailSpringSettings, 'confirmedBy'>;

export interface TailSpringView extends TailSpringChoice {
  readonly available: boolean;
  readonly source: TailSpringSource | null;
  /** Where the settings come from: the Document, the file's declaration, or the prior's defaults. */
  readonly settingsFrom: 'document' | 'file' | 'prior';
  /** A file spring with no drive block and no Document settings plays the plain passive reference. */
  readonly passive: boolean;
  /** `knobs` spelled as the declaration's provenance (the three original sliders). */
  readonly knobs: SpringKnobs;
  readonly joints: number;
  /** The per-sprung-joint drive gains for the current mood and muscle, root → tip. */
  readonly gains: readonly number[];
  readonly colliders: readonly { readonly bone: string; readonly kind: string; readonly radiusCm: number; readonly note: string; /** Dropped or shrunk by the fit (the Body header's `check`). */ readonly adjusted: boolean }[];
  /** Why a grown tail could not be sprung (a refusal sentence), or null. */
  readonly refusal: string | null;
  /** A flat tail's hinge (ADR-038 §13.7): its median cross-section aspect, or null for a round tail. */
  readonly paddle: { readonly aspect: number } | null;
}

export interface TailSpringProbe {
  readonly on: boolean;
  readonly source: TailSpringSource | null;
  readonly mood: string;
  readonly muscle: number;
  readonly settingsFrom: TailSpringView['settingsFrom'] | null;
  readonly frames: number;
  /** Frames drawn with the chain back AT REST although the spring had a solution — the "two tails" flicker; must stay 0. */
  readonly restFrames: number;
  readonly steps: number;
  readonly joints: number;
  readonly colliders: number;
  /** Scrubs / clip restarts that reset and pre-rolled the chain. */
  readonly prerolls: number;
  readonly teleports: number;
  /** Joint-steps set back on their cone. */
  readonly coneClamps: number;
  /** What the fit decided, collider by collider (the Body card's list) — the census reads it. */
  readonly fitted: TailSpringView['colliders'];
  readonly knobs: SpringKnobs;
}

interface Entry {
  source: TailSpringSource;
  base: SpringDeclaration;
  report: TailColliderReport | null;
  runtime: SpringRuntime | null;
  axes: DriveAxes[] | null;
  /** The paddle hinge, measured once from the flesh (grown) or read from the file; undefined = not yet measured. */
  hinge: TailHinge | null | undefined;
  drive: TailDrive | null;
  /** The settings the runtime and drive last wore, as a value key (compared per frame). */
  applied: string | null;
  /** The drive's settings key (a mood change cross-fades; a knob applies at once). */
  driveKey: string | null;
  /** The drive the runtime is layered over (null = the plain reference). */
  layered: TailDrive | null;
  /** The drive clock when the spring is off (kinematic). */
  kinematicSeconds: number;
}

const fileName = (n: string): string => n.replace(/_graft$/, '');

function skinnedMeshOf(scene: Object3D, bone: string): SkinnedMesh | null {
  let found: SkinnedMesh | null = null;
  scene.traverse((o) => {
    const m = o as SkinnedMesh;
    if (found === null && m.isSkinnedMesh === true && m.skeleton.bones.some((b) => b.name === bone)) found = m;
  });
  return found;
}

/** The prior's settings for a grown tail. */
export const PRIOR_TAIL_SPRING: TailSpringChoice = {
  on: true,
  ...TAIL_SPRING_KNOBS,
  muscle: TAIL_MUSCLE_DEFAULT,
  mood: TAIL_MOOD_DEFAULT,
  wag: TAIL_WAG_DEFAULTS,
  inertia: TAIL_DYNAMICS_DEFAULTS.inertia,
  reflex: false,
};

/**
 * The tail prior's declaration for a grown tail, colliders fitted AT BIND
 * (the scene may be posed by the mixer; the skeleton is put on its bind for the
 * measurement and every bone's TRS restored after, bit-for-bit).
 */
export function grownTailSpring(run: TailSpringRun, knobs: SpringKnobs = TAIL_SPRING_KNOBS): { decl: SpringDeclaration; report: TailColliderReport } {
  const tail = run.tailGrafts[0];
  if (tail === undefined) throw new Error('grownTailSpring: no tail was grown on this run');
  return chainSpring(run, tail, knobs);
}

const TAIL_NAME = /tail/i;

/**
 * The tail the FILE rigs: the longest chain of bones named tail, from a tail bone whose parent is not
 * one, child by child. Null when there is none (fewer than two bones: nothing to sway).
 */
export function nativeTailChain(scene: Object3D): string[] | null {
  const tails: Object3D[] = [];
  scene.traverse((o) => {
    if ((o as { isBone?: boolean }).isBone === true && TAIL_NAME.test(o.name) && !o.name.endsWith('_graft')) tails.push(o);
  });
  const isTail = (o: Object3D | null): boolean => o !== null && tails.includes(o);
  let best: string[] = [];
  for (const root of tails.filter((t) => !isTail(t.parent))) {
    const chain: string[] = [];
    for (let at: Object3D | undefined = root; at !== undefined; ) {
      chain.push(at.name);
      const next: Object3D[] = at.children.filter((c) => isTail(c));
      at = next.length === 0 ? undefined : next.reduce((a, b) => (countTail(b) > countTail(a) ? b : a));
    }
    if (chain.length > best.length) best = chain;
  }
  function countTail(o: Object3D): number {
    let n = 0;
    o.traverse((c) => {
      if (isTail(c)) n += 1;
    });
    return n;
  }
  return best.length >= 2 ? best : null;
}

/**
 * Per-joint hit radii for a chain the file rigs (the graft records them; a file does not): the median
 * distance, from the bone's segment, of the vertices whose heaviest influence is that bone — measured
 * at BIND (the caller has posed the skeleton); then the tip's, 0.7 of the last bone's.
 */
function measureChainRadii(mesh: SkinnedMesh, bones: readonly Object3D[]): number[] {
  const g = mesh.geometry;
  const index = g.getAttribute('skinIndex');
  const weight = g.getAttribute('skinWeight');
  const position = g.getAttribute('position');
  const slot = new Map<number, number>();
  bones.forEach((b, k) => {
    const i = mesh.skeleton.bones.indexOf(b as never);
    if (i >= 0) slot.set(i, k);
  });
  const joints = bones.map((b) => b.getWorldPosition(new Vector3()));
  const dists: number[][] = bones.map(() => []);
  const v = new Vector3();
  const seg = new Vector3();
  const rel = new Vector3();
  for (let i = 0; i < position.count; i++) {
    let best = -1;
    let bestW = 0;
    for (let c = 0; c < 4; c++) {
      const w = weight.getComponent(i, c);
      if (w > bestW) {
        bestW = w;
        best = index.getComponent(i, c);
      }
    }
    const k = slot.get(best);
    if (k === undefined) continue;
    mesh.getVertexPosition(i, v);
    v.applyMatrix4(mesh.matrixWorld);
    const a = joints[k]!;
    const b = joints[Math.min(k + 1, joints.length - 1)]!;
    seg.subVectors(b, a);
    const len2 = seg.lengthSq();
    rel.subVectors(v, a);
    const t = len2 > 0 ? Math.max(0, Math.min(1, rel.dot(seg) / len2)) : 0;
    dists[k]!.push(rel.sub(seg.multiplyScalar(t)).length());
    seg.subVectors(b, a);
  }
  const median = (xs: number[]): number | null => {
    if (xs.length === 0) return null;
    const s = [...xs].sort((p, q) => p - q);
    return s[Math.floor(s.length / 2)]!;
  };
  const radii = dists.map(median);
  const fallback = radii.find((r) => r !== null) ?? joints[0]!.distanceTo(joints[joints.length - 1]!) / (joints.length * 4);
  const out: number[] = [];
  for (let k = 0; k < radii.length; k++) out.push(Math.max(radii[k] ?? out[k - 1] ?? fallback, 1e-3));
  // The declaration takes a radius per joint AND the tip beyond the last bone (a grown tail records count + 1):
  // the tip narrows as a tail does.
  out.push(Math.max(out[out.length - 1]! * 0.7, 1e-3));
  return out;
}

/** A chain's spring: the tail prior's declaration, colliders fitted from this character's flesh at bind. */
function chainSpring(run: TailSpringRun, tail: { readonly bones: readonly string[]; readonly radii: readonly number[] | null }, knobs: SpringKnobs): { decl: SpringDeclaration; report: TailColliderReport } {
  const bone = (o: string): string | undefined => run.rig.mapping.find((m) => m.ontologyId === o)?.rigBone;
  const hips = bone('Hips');
  const legs = (['Left', 'Right'] as const).map((s) => ({ thigh: bone(`${s}UpLeg`), knee: bone(`${s}Leg`) }));
  if (hips === undefined || legs.some((l) => l.thigh === undefined || l.knee === undefined)) {
    throw new Error('the rig maps no hips and two legs for the tail to collide with');
  }
  const mesh = skinnedMeshOf(run.scene, tail.bones[0]!);
  if (mesh === null) throw new Error(`no skinned mesh is bound to ${tail.bones[0]}`);
  const saved: { o: Object3D; p: Vector3; q: Quaternion; s: Vector3 }[] = [];
  run.scene.traverse((o) => saved.push({ o, p: o.position.clone(), q: o.quaternion.clone(), s: o.scale.clone() }));
  try {
    mesh.skeleton.pose();
    run.scene.updateMatrixWorld(true);
    const bones = tail.bones.map((n) => run.scene.getObjectByName(n)!);
    const radii = tail.radii ?? measureChainRadii(mesh, bones);
    const tailPoints = bones.slice(1).map((b, k) => ({ position: b.getWorldPosition(new Vector3()), hitRadius: radii[k + 1]! }));
    const report = fitTailColliders({
      root: run.scene,
      mesh,
      hips,
      ...(bone('Spine') !== undefined ? { spine: bone('Spine')! } : {}),
      tailRoot: tail.bones[0]!,
      legs: legs.map((l) => ({ thigh: l.thigh!, knee: l.knee! })),
      tailPoints,
      fileName,
    });
    // The stability bound's input: each sprung joint's segment at bind (bone → child, world).
    const segmentLengths = bones.slice(0, -1).map((b, k) => b.getWorldPosition(new Vector3()).distanceTo(bones[k + 1]!.getWorldPosition(new Vector3())));
    const decl = tailSpringDeclaration({ bones: tail.bones.map(fileName), radii, colliders: report.colliders, knobs, segmentLengths });
    return { decl, report };
  } finally {
    for (const { o, p, q, s } of saved) {
      o.position.copy(p);
      o.quaternion.copy(q);
      o.scale.copy(s);
    }
    run.scene.updateMatrixWorld(true);
  }
}

export interface TailSpringDeps {
  /** The Document's `edits.tailSpring` now (absent = the source decides). */
  readonly document: () => TailSpringSettings | undefined;
  /** Write one `tailSpring.set` (the log's door). */
  readonly commit: (settings: TailSpringSettings) => void;
  /** The floor's height under the character (the viewport's shadow catcher) — tails rest on it as World's rest on its ground contact. Null = none. */
  readonly floorY?: () => number | null;
}

export interface TailSpringClock {
  /** The viewport's clip clock (seconds), and whether it is paused. */
  readonly clipSeconds: number;
  readonly paused: boolean;
}

export interface TailSpring {
  view(run: TailSpringRun): TailSpringView;
  setOn(run: TailSpringRun, on: boolean, confirmedBy: string): void;
  /** A live slider drag: previewed, never committed (null clears the preview). */
  preview(run: TailSpringRun, choice: Partial<TailSpringChoice> | null): void;
  /** One `tailSpring.set` with these choices over the effective settings. */
  commit(run: TailSpringRun, choice: Partial<TailSpringChoice>, confirmedBy: string): void;
  /** Put the tail back on its target and forget its velocity (the card's "settle"). */
  reset(run: TailSpringRun): void;
  /** Does the spring play on this run right now (a chain, switched on)? */
  playing(run: TailSpringRun): boolean;
  /** This frame's spring over the live chain, or null — handed to the render-preview slot. */
  applyFrame(run: TailSpringRun, frameDt: number, clock?: TailSpringClock): (() => void) | null;
  /** The spring OFF: the drive's offsets for this frame (the kinematic mood), or null when there is no chain or no drive. */
  kinematicOffsets(run: TailSpringRun, frameDt: number): { bones: Object3D[]; offsets: Quaternion[] } | null;
  /** What the export door writes: the declaration (with its drive block) when on, nothing when off. */
  springsForExport(run: TailSpringRun): readonly SpringDeclaration[];
  probe(run: TailSpringRun): TailSpringProbe;
  /** The bone OBJECTS the running solver writes (empty before its first frame). */
  springBones(run: TailSpringRun): readonly Object3D[];
}

const choiceOf = (s: TailSpringSettings): TailSpringChoice => {
  const c: Partial<TailSpringSettings> = { ...s };
  delete c.confirmedBy;
  return c as TailSpringChoice;
};

export function createTailSpring(deps: TailSpringDeps): TailSpring {
  const entries = new WeakMap<Object3D, Entry | { refusal: string }>();
  let frames = 0;
  let restFrames = 0;
  let prerolls = 0;
  let preview: Partial<TailSpringChoice> | null = null;
  let lastClip: number | null = null;
  // GROUND CONTACT: a horizontal plane at the viewport's floor (one object, its height refreshed per ask).
  const floorPlane = { point: new Vector3(), normal: new Vector3(0, 1, 0) };
  const floorY = deps.floorY;
  const ground =
    floorY === undefined
      ? null
      : (): typeof floorPlane | null => {
          const y = floorY();
          if (y === null) return null;
          floorPlane.point.y = y;
          return floorPlane;
        };

  const entryOf = (run: TailSpringRun): Entry | { refusal: string } | null => {
    const cached = entries.get(run.scene);
    if (cached !== undefined) return cached;
    let made: Entry | { refusal: string } | null = null;
    const fresh = { runtime: null, axes: null, hinge: undefined, drive: null, applied: null, driveKey: null, layered: null, kinematicSeconds: 0 };
    if (run.tailGrafts.length > 0) {
      try {
        const { decl, report } = grownTailSpring(run);
        made = { source: 'grown', base: decl, report, ...fresh };
      } catch (err) {
        made = { refusal: `tail spring refused: ${err instanceof Error ? err.message : String(err)}` };
      }
    } else {
      const first = declaredSprings(run.scene)[0];
      if (first !== undefined) made = { source: 'file', base: first, report: null, ...fresh };
      else {
        const chain = nativeTailChain(run.scene);
        if (chain !== null) {
          try {
            const { decl, report } = chainSpring(run, { bones: chain, radii: null }, TAIL_SPRING_KNOBS);
            made = { source: 'native', base: decl, report, ...fresh };
          } catch (err) {
            made = { refusal: `tail spring refused: ${err instanceof Error ? err.message : String(err)}` };
          }
        }
      }
    }
    if (made !== null) entries.set(run.scene, made);
    return made;
  };
  const live = (run: TailSpringRun): Entry | null => {
    const e = entryOf(run);
    return e === null || 'refusal' in e ? null : e;
  };

  /** The settings in force: the drag preview over the Document over the source's own. */
  const settingsOf = (e: Entry): { choice: TailSpringChoice; from: TailSpringView['settingsFrom']; passive: boolean; confirmedBy: string | undefined } => {
    const doc = deps.document();
    let choice: TailSpringChoice;
    let from: TailSpringView['settingsFrom'];
    let passive = false;
    if (doc !== undefined) {
      choice = choiceOf(doc);
      from = 'document';
    } else if (e.source === 'file') {
      const d = e.base.drive;
      const k = e.base.provenance.knobs;
      // A spring HARRY wrote without a drive block (every tail exported before
      // #190 — the ten on World's carousel, 2026-09-25) takes the default
      // drive: Allen, "apply it across the board — the defaults have been dead
      // on". Only a spring another tool wrote (`imported`: a VRoid's hair)
      // stays the plain passive reference.
      const ours = e.base.provenance.rule !== 'imported';
      choice = {
        on: true,
        ...k,
        muscle: d?.muscle ?? (ours ? TAIL_MUSCLE_DEFAULT : 0),
        mood: d?.mood ?? (ours ? TAIL_MOOD_DEFAULT : 'loose'),
        wag: d?.wag ?? TAIL_WAG_DEFAULTS,
        inertia: d?.dynamics.inertia ?? TAIL_DYNAMICS_DEFAULTS.inertia,
        reflex: d?.reflex ?? false,
      };
      from = 'file';
      passive = d === undefined && !ours;
    } else {
      // A tail the file rigs is OFFERED, never switched on for the person (offers never act).
      choice = e.source === 'native' ? { ...PRIOR_TAIL_SPRING, on: false } : PRIOR_TAIL_SPRING;
      from = 'prior';
    }
    if (preview !== null) {
      choice = { ...choice, ...preview, wag: { ...choice.wag, ...preview.wag } };
      passive = false;
    }
    return { choice, from, passive, confirmedBy: doc?.confirmedBy };
  };
  const knobsOf = (c: TailSpringChoice): SpringKnobs => ({ stiffness: c.stiffness, drag: c.drag, gravity: c.gravity });
  const sameKnobs = (a: SpringKnobs, b: SpringKnobs): boolean => a.stiffness === b.stiffness && a.drag === b.drag && a.gravity === b.gravity;
  /** The declaration the settings make: the base retuned when a knob differs from what it was built with. */
  const declarationOf = (e: Entry, c: TailSpringChoice, confirmedBy: string | undefined): SpringDeclaration =>
    sameKnobs(knobsOf(c), e.base.provenance.knobs) ? e.base : retuneTailSpring(e.base, knobsOf(c), confirmedBy ?? e.base.provenance.confirmedBy ?? 'human:local');
  const bonesOf = (run: TailSpringRun, e: Entry): Object3D[] | null => {
    const found = e.base.joints.map((j) => run.scene.getObjectByName(j.bone) ?? run.scene.getObjectByName(`${j.bone}_graft`));
    return found.every((b) => b !== undefined) ? (found as Object3D[]) : null;
  };
  const axesOf = (run: TailSpringRun, e: Entry): DriveAxes[] | null => {
    if (e.axes !== null) return e.axes;
    if (e.base.drive !== undefined && e.source === 'file') {
      e.axes = e.base.drive.axes.map((a) => ({ yaw: [...a.yaw], pitch: [...a.pitch] }));
      return e.axes;
    }
    const bones = bonesOf(run, e);
    if (bones === null) return null;
    e.axes = measureDriveAxes(run.scene, bones);
    return e.axes;
  };
  /** A flat tail's hinge: the file's declaration, else measured once from this character's flesh. */
  const hingeOf = (run: TailSpringRun, e: Entry): TailHinge | null => {
    if (e.hinge !== undefined) return e.hinge;
    if (e.source === 'file') e.hinge = e.base.drive?.hinge ?? null;
    else {
      const bones = bonesOf(run, e);
      e.hinge = bones === null ? null : tailHingeFromFlatness(measureTailFlatness(run.scene, bones));
    }
    return e.hinge;
  };
  const driveOf = (run: TailSpringRun, e: Entry, c: TailSpringChoice, seconds: number): TailDrive | null => {
    const axes = axesOf(run, e);
    if (axes === null) return null;
    const key = JSON.stringify(c);
    if (e.drive === null) e.drive = createTailDrive(axes, c, hingeOf(run, e));
    else if (e.driveKey !== key) e.drive.update(c, seconds);
    e.driveKey = key;
    return e.drive;
  };

  const api: TailSpring = {
    view(run) {
      const e = entryOf(run);
      const none = { ...PRIOR_TAIL_SPRING, on: false, knobs: TAIL_SPRING_KNOBS, joints: 0, gains: [], colliders: [], source: null, settingsFrom: 'prior' as const, passive: false, paddle: null };
      if (e === null) return { ...none, available: false, refusal: null };
      if ('refusal' in e) return { ...none, available: false, refusal: e.refusal };
      const s = settingsOf(e);
      const d = declarationOf(e, s.choice, s.confirmedBy);
      const colliders =
        e.report !== null
          ? e.report.fitted.map((f) => ({
              bone: f.bone,
              kind: f.kind,
              radiusCm: +(f.radius * 100).toFixed(1),
              adjusted: f.dropped || f.clippedBy !== null,
              note:
                (f.dropped ? 'dropped — the tail starts too close' : f.clippedBy !== null ? `shrunk from ${(f.fleshRadius * 100).toFixed(1)} cm to clear the tail` : 'fitted to the flesh') +
                (f.slide !== undefined && f.slide > 0 ? `, centre ${(f.slide * 100).toFixed(1)} cm off the joint, away from the tail` : ''),
            }))
          : d.colliders.map((c) => ({ bone: c.bone, kind: 'sphere' in c.shape ? 'sphere' : 'capsule', radiusCm: +(('sphere' in c.shape ? c.shape.sphere.radius : c.shape.capsule.radius) * 100).toFixed(1), note: 'as the file declares', adjusted: false }));
      return {
        ...s.choice,
        available: true,
        source: e.source,
        settingsFrom: s.from,
        passive: s.passive,
        knobs: knobsOf(s.choice),
        joints: d.joints.length,
        gains: s.passive ? [] : moodGains(s.choice.mood, s.choice.muscle, d.joints.length - 1),
        colliders,
        refusal: null,
        paddle: s.passive ? null : (() => { const h = hingeOf(run, e); return h === null ? null : { aspect: +[...h.aspect].sort((x, y) => x - y)[Math.floor(h.aspect.length / 2)]!.toFixed(2) }; })(),
      };
    },
    setOn(run, on, confirmedBy) {
      api.commit(run, { on }, confirmedBy);
      live(run)?.runtime?.reset();
    },
    preview(_run, choice) {
      preview = choice;
    },
    commit(run, choice, confirmedBy) {
      const e = live(run);
      if (e === null) return;
      const base = settingsOf(e).choice;
      preview = null;
      deps.commit({ ...base, ...choice, wag: { ...base.wag, ...choice.wag }, confirmedBy });
    },
    reset(run) {
      live(run)?.runtime?.reset();
    },
    playing(run) {
      const e = live(run);
      return e !== null && settingsOf(e).choice.on;
    },
    applyFrame(run, frameDt, clock) {
      const e = live(run);
      if (e === null) return null;
      const s = settingsOf(e);
      if (!s.choice.on) return null;
      const decl = declarationOf(e, s.choice, s.confirmedBy);
      if (e.runtime === null) e.runtime = createSpringRuntime(run.scene, decl, ground === null ? {} : { ground });
      const rt = e.runtime;
      const key = `${s.passive}|${JSON.stringify(s.choice)}`;
      if (e.applied !== key) {
        rt.retune(decl);
        const want = s.passive ? null : driveOf(run, e, s.choice, rt.time);
        // A new drive object only when layering starts or stops; a settings change updates the drive in place (its state kept).
        if (want !== e.layered) rt.setDrive(want);
        e.layered = want;
        e.applied = key;
      }
      const atRest = rt.bones.map((b) => b.quaternion.clone());
      const restore = rt.hold();
      // A scrub, a paused frame moved, or a clip restart is a teleport: reset onto the target and pre-roll.
      if (clock !== undefined) {
        const jumped =
          lastClip !== null &&
          (clock.paused ? clock.clipSeconds !== lastClip : clock.clipSeconds < lastClip - 1e-6 || clock.clipSeconds > lastClip + Math.max(frameDt, 0) + 0.25);
        lastClip = clock.clipSeconds;
        if (jumped) {
          rt.preroll(0.5);
          prerolls += 1;
        }
      }
      rt.advance(Math.min(Math.max(frameDt, 0), 0.25));
      frames += 1;
      if (rt.probe().steps > 0 && rt.bones.every((b, k) => b.quaternion.equals(atRest[k]!))) restFrames += 1;
      return restore;
    },
    kinematicOffsets(run, frameDt) {
      const e = live(run);
      if (e === null) return null;
      const s = settingsOf(e);
      const bones = bonesOf(run, e);
      if (bones === null) return null;
      e.kinematicSeconds += Math.min(Math.max(frameDt, 0), 0.25);
      const drive = driveOf(run, e, s.choice, e.kinematicSeconds);
      if (drive === null) return null;
      const offsets = bones.map(() => new Quaternion());
      drive.offsets(e.kinematicSeconds, offsets);
      return { bones, offsets };
    },
    springsForExport(run) {
      const e = live(run);
      if (e === null) return [];
      const s = settingsOf(e);
      if (!s.choice.on) return [];
      const decl = declarationOf(e, s.choice, s.confirmedBy);
      if (s.passive) return [decl];
      const axes = axesOf(run, e);
      return axes === null ? [decl] : [{ ...decl, drive: springDriveDeclaration(s.choice, axes, hingeOf(run, e)) }];
    },
    springBones(run) {
      return live(run)?.runtime?.bones ?? [];
    },
    probe(run) {
      const e = live(run);
      const p = e?.runtime?.probe();
      const s = e === null ? null : settingsOf(e);
      return {
        on: s?.choice.on ?? false,
        source: e?.source ?? null,
        mood: s?.choice.mood ?? TAIL_MOOD_DEFAULT,
        muscle: s?.choice.muscle ?? TAIL_MUSCLE_DEFAULT,
        settingsFrom: s?.from ?? null,
        frames,
        restFrames,
        steps: p?.steps ?? 0,
        joints: p?.joints ?? 0,
        colliders: p?.colliders ?? 0,
        prerolls,
        teleports: p?.teleports ?? 0,
        coneClamps: p?.coneClamps ?? 0,
        fitted: api.view(run).colliders,
        knobs: s === null ? TAIL_SPRING_KNOBS : knobsOf(s.choice),
      };
    },
  };
  return api;
}
